import { lookupTableRate, purchaseTotal, roundDecimal, trimRate } from '@mms/shared';
import { useEffect, useRef, type ReactNode } from 'react';
import { Alert } from 'react-bootstrap';
import { z } from 'zod';
import type { Row } from '../api/types';
import { amountReq, dateTimeReq, rateReq, selectOpt, selectReq, toId, toIdOrNull, toText } from '../forms/zod';
import { BalanceCard } from '../ui/BalanceCard';
import type { Column } from '../ui/DataTable';
import { Amount, fromDateTimeInput, nowInput, toDateTimeInput } from '../ui/format';
import { toOptions, useLatestRates, useLookup } from '../ui/lookups';
import { Segmented } from '../ui/Segmented';
import { FieldRenderer, type CrudConfig, type FieldDef } from './CrudScreen';
import { TROUGH_BANK, TROUGH_CASH, base, dateCol, noCol, useBankPicker } from './transactions';

/**
 * Purchase (redesigned 2026-10-13, migration 022). You PAY the Seller `amount` in the Pay currency and RECEIVE `amount x rate` in a
 * different Receive currency into Cash, a Bank, or a Dispatcher's account. Every purchase also pays the Seller out of Cash.
 * Form values: currencyId = Pay currency, supplierCurrencyId = Receive currency (the API keeps those column names), and
 * troughId '1' Cash / '2' Bank / DISPATCHER (form-only) which is sent as a null trough with a supplierId.
 */
const DISPATCHER = '3';
const sameCurrency0 = (a: string, b: string) => !!a && a === b;
const toTrough = (v: string) => (v === DISPATCHER ? null : toId(v));

const purchaseSchema = z
  .object({
    currencyId: selectReq('Select the pay currency'), purchaserId: selectReq('Select a seller'),
    supplierCurrencyId: selectReq('Select the receive currency'),
    troughId: z.string(), bankId: selectOpt, supplierId: selectOpt,
    amount: amountReq, exchangeRate: rateReq, transactionDate: dateTimeReq, remarks: z.string(),
  })
  .superRefine((v, ctx) => {
    if (v.currencyId && v.currencyId === v.supplierCurrencyId) ctx.addIssue({ code: 'custom', path: ['supplierCurrencyId'], message: 'Pay and receive currencies must be different' });
    if (v.troughId === TROUGH_BANK && !v.bankId) ctx.addIssue({ code: 'custom', path: ['bankId'], message: 'Select a bank' });
    if (v.troughId === DISPATCHER && !v.supplierId) ctx.addIssue({ code: 'custom', path: ['supplierId'], message: 'Select a dispatcher' });
  });

/** A seller / dispatcher lookup row holds a currency? */
const holds = (p: Row, currencyId: string) => ((p.currencies as Row[] | undefined) ?? []).some((c) => String(c.currencyId) === currencyId);

export const purchasesConfig: CrudConfig = {
  ...base,
  title: 'Purchases', singular: 'purchase', icon: 'cart-check', menu: 'purchase', endpoint: '/transactions/purchase',
  modalSize: 'lg',
  schema: purchaseSchema,
  defaults: () => ({ currencyId: '', purchaserId: '', supplierCurrencyId: '', troughId: TROUGH_CASH, bankId: '', supplierId: '', amount: '', exchangeRate: '', transactionDate: nowInput(), remarks: '' }),
  toForm: (r) => ({
    currencyId: String(r.currencyId), purchaserId: String(r.purchaserId), supplierCurrencyId: String(r.supplierCurrencyId),
    troughId: r.troughId == null ? DISPATCHER : String(r.troughId), bankId: r.bankId ? String(r.bankId) : '', supplierId: r.supplierId ? String(r.supplierId) : '',
    amount: r.amount, exchangeRate: String(Number(r.exchangeRate)), transactionDate: toDateTimeInput(r.transactionDate), remarks: r.remarks ?? '',
  }),
  toPayload: (v) => ({
    purchaserId: toId(v.purchaserId), currencyId: toId(v.currencyId), supplierCurrencyId: toId(v.supplierCurrencyId), amount: v.amount.trim(), exchangeRate: v.exchangeRate.trim(),
    troughId: toTrough(v.troughId), bankId: v.troughId === TROUGH_BANK ? toIdOrNull(v.bankId) : null, supplierId: v.troughId === DISPATCHER ? toIdOrNull(v.supplierId) : null,
    transactionDate: fromDateTimeInput(v.transactionDate), remarks: toText(v.remarks),
  }),
  rowLabel: (r) => `${r.transactionNo} (${r.purchaserName})`,
  useTable: () => {
    const purchasers = useLookup('purchasers');
    const suppliers = useLookup('suppliers');
    // #Ref, Date, Seller, You pay (Pay currency), Rate, You receive (Receive currency), Received into
    const columns: Column[] = [
      { ...noCol, header: '#Ref', hideOnMobile: false },
      dateCol,
      { key: 'purchaserName', header: 'Seller', sortKey: 'purchaser', className: 'fw-semibold' },
      { key: 'amount', header: 'You pay', sortKey: 'amount', align: 'end', render: (r) => <Amount value={r.amount} code={r.currencyCode} /> },
      {
        key: 'exchangeRate', header: 'Rate', align: 'end', hideOnMobile: true,
        render: (r) => <>{Number(r.exchangeRate).toString()}{r.supplierCurrencyCode ? <small className="text-body-secondary ms-1">{r.supplierCurrencyCode}</small> : null}</>,
      },
      { key: 'total', header: 'You receive', sortKey: 'total', align: 'end', render: (r) => <Amount value={r.total} code={r.supplierCurrencyCode} /> },
      {
        key: 'trough', header: 'Received into', hideOnMobile: true,
        render: (r) => (r.troughId == null
          ? <>Dispatcher<small className="text-body-secondary ms-1">{r.supplierName}</small></>
          : r.bankName ? <>{r.trough}<small className="text-body-secondary ms-1">{r.bankName}</small></> : <>{r.trough}</>),
      },
    ];
    return {
      columns,
      filters: [
        { param: 'purchaserId', label: 'Seller', options: toOptions(purchasers.data) },
        { param: 'supplierId', label: 'Dispatcher', options: toOptions(suppliers.data) },
      ],
    };
  },
  useForm: (form, { readOnly, mode }) => {
    const currencies = useLookup('currencies');
    const rates = useLatestRates();
    const purchasers = useLookup('purchasers');
    const suppliers = useLookup('suppliers');
    const payId = form.watch('currencyId') as string;
    const receiveId = form.watch('supplierCurrencyId') as string;
    const purchaserId = form.watch('purchaserId') as string;
    const supplierId = form.watch('supplierId') as string;
    const trough = String(form.watch('troughId') ?? '');
    const bankId = form.watch('bankId') as string;
    const amount = String(form.watch('amount') ?? '');
    const rate = String(form.watch('exchangeRate') ?? '');
    const code = (id: string) => currencies.data?.find((c) => String(c.id) === id)?.code as string | undefined;
    const payCode = code(payId);
    const receiveCode = code(receiveId);

    // The Rate comes from the Exchange Rate table, the WHOLESALE column (purchases are wholesale deals; invoices use the retail one): 1 Pay = ? Receive, through the
    // base currency flagged on the Currency table (same pair logic as the invoice). It can still be edited; it is held to the 4 decimals a purchase rate allows.
    const baseId = currencies.data?.find((c) => c.isBase)?.id as number | undefined;
    const tableRate = (() => {
      if (!payId || !receiveId) return null;
      const r = lookupTableRate((rates.data ?? []).map((x) => ({ currencyId: x.currencyId, rate1: x.rate2 })), baseId, Number(payId), Number(receiveId));
      const rounded = r ? roundDecimal(r, 4) : null;
      return rounded && Number(rounded) > 0 ? trimRate(rounded) : null;
    })();
    const lastPair = useRef(`${payId}|${receiveId}`);
    useEffect(() => {
      const pair = `${payId}|${receiveId}`;
      const changed = pair !== lastPair.current;
      lastPair.current = pair;
      // a new pair resets the rate to the table; a new purchase whose rates arrived late fills an empty rate; an opened purchase keeps its saved rate
      if (changed) form.setValue('exchangeRate', tableRate ?? '', { shouldValidate: form.formState.isSubmitted });
      else if (mode === 'create' && tableRate && !String(form.getValues('exchangeRate') ?? '')) form.setValue('exchangeRate', tableRate);
    }, [payId, receiveId, tableRate]); // eslint-disable-line react-hooks/exhaustive-deps
    const rateEdited = !!tableRate && !!rate && Number(rate) !== Number(tableRate);
    const rateNote = !payId || !receiveId || sameCurrency0(payId, receiveId) ? null : tableRate === null && !rates.isPending
      ? <div className="text-danger small mt-n2 mb-2">No wholesale rate in the Exchange Rate table for this pair. Enter it.</div>
      : tableRate ? <div className={`small mt-n2 mb-2 ${rateEdited ? 'text-warning' : 'text-body-secondary'}`} data-testid="rate-note">{rateEdited ? 'Edited. ' : ''}Wholesale rate {tableRate}</div> : null;
    const sameCurrency = !!payId && payId === receiveId;

    // Sellers who hold the Pay currency; dispatchers who hold the Receive currency
    const sellerRows = payId ? (purchasers.data ?? []).filter((p) => holds(p, payId)) : [];
    const dispatcherRows = receiveId ? (suppliers.data ?? []).filter((d) => holds(d, receiveId)) : [];
    const picker = useBankPicker(form, receiveId ? Number(receiveId) : undefined); // a Bank must be in the Receive currency
    const bank = picker.fields()[1] as FieldDef;
    const bankName = bank.options?.find((o) => o.value === bankId)?.label;
    const sellerName = (purchasers.data ?? []).find((p) => String(p.id) === purchaserId)?.name as string | undefined;
    const dispatcherName = (suppliers.data ?? []).find((d) => String(d.id) === supplierId)?.name as string | undefined;

    // drop a seller / dispatcher that no longer holds the chosen currency
    useEffect(() => {
      if (purchaserId && payId && !sellerRows.some((p) => String(p.id) === purchaserId)) form.setValue('purchaserId', '');
    }, [payId, purchaserId, purchasers.data]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => {
      if (supplierId && (trough !== DISPATCHER || (receiveId && !dispatcherRows.some((d) => String(d.id) === supplierId)))) form.setValue('supplierId', '');
    }, [receiveId, supplierId, trough, suppliers.data]); // eslint-disable-line react-hooks/exhaustive-deps

    const total = sameCurrency ? null : purchaseTotal(amount, rate);
    const field = (def: FieldDef) => <FieldRenderer form={form} field={{ ...def, col: 12 }} readOnly={readOnly} />;
    const currencyOptions = toOptions(currencies.data, (c) => c.code as string);
    const accent = (color: string, title: string, children: ReactNode, right?: ReactNode) => (
      <div className="card h-100" style={{ borderLeft: `3px solid var(--bs-${color})` }}>
        <div className="card-body py-2 px-3">
          <div className="d-flex justify-content-between align-items-center gap-2 mb-2" style={{ minHeight: 31 }}>
            <div className={`fw-semibold text-nowrap text-${color}`}>{title}</div>
            {right}
          </div>
          {children}
        </div>
      </div>
    );

    const body = (
      <div className="mms-compact">
        {sameCurrency && (
          <Alert variant="danger" className="py-2 px-3 mb-3 d-flex align-items-center" data-testid="currency-error">
            <i className="bi bi-exclamation-triangle-fill me-2" aria-hidden />
            <strong>Pay and receive currencies must be different.</strong>
          </Alert>
        )}
        <div className="row g-2 mb-2">
          <div className="col-lg-6">
            {accent('warning', 'Buy from (you pay)', (
              <>
                <div className="row">
                  <div className="col-sm-5">{field({ name: 'currencyId', label: 'Pay currency', type: 'select', required: true, options: currencyOptions, placeholder: 'Select…' })}</div>
                  <div className="col-sm-7">
                    {field({
                      name: 'purchaserId', label: `Seller${payCode ? ` (${payCode} only)` : ''}`, type: 'select', required: true, options: toOptions(sellerRows),
                      placeholder: payId ? 'Select…' : 'Pay currency first', disabled: !payId,
                    })}
                  </div>
                </div>
                <BalanceCard kind="purchasers" id={purchaserId} currencyId={payId} title="Seller balance" />
              </>
            ))}
          </div>
          <div className="col-lg-6">
            {accent('success', 'Received into (you receive)', (
              <>
                <div className="row">
                  <div className="col-sm-5">{field({ name: 'supplierCurrencyId', label: 'Receive currency', type: 'select', required: true, options: currencyOptions, placeholder: 'Select…' })}</div>
                  <div className="col-sm-7">
                    {trough === DISPATCHER && field({
                      name: 'supplierId', label: `Dispatcher${receiveCode ? ` (${receiveCode} only)` : ''}`, type: 'select', required: true, options: toOptions(dispatcherRows),
                      placeholder: receiveId ? 'Select…' : 'Receive currency first', disabled: !receiveId,
                    })}
                    {trough === TROUGH_BANK && field({ ...bank, label: `Bank${receiveCode ? ` (${receiveCode} only)` : ''}`, required: true, disabled: !receiveId, placeholder: receiveId ? 'Select a bank…' : 'Receive currency first' })}
                    {trough === TROUGH_CASH && <div className="text-body-secondary small pt-4">Cash in hand{receiveCode ? ` (${receiveCode})` : ''}</div>}
                  </div>
                </div>
                {trough === DISPATCHER && <BalanceCard kind="suppliers" id={supplierId} currencyId={receiveId} title="Dispatcher balance" />}
                {trough === TROUGH_BANK && <BalanceCard kind="banks" id={bankId} title="Bank balance" />}
              </>
            ), (
              <Segmented form={form} name="troughId" label="Received into" hideLabel compact disabled={readOnly}
options={[
{ value: TROUGH_CASH, label: 'Cash', variant: 'success', icon: 'cash-coin' },
{ value: TROUGH_BANK, label: 'Bank', variant: 'success', icon: 'bank' },
{ value: DISPATCHER, label: 'Dispatcher', variant: 'success', icon: 'truck' },
]} />
            ))}
          </div>
        </div>
        <div className="card">
          <div className="card-body py-2 px-3">
            <div className="fw-semibold mb-1">The deal</div>
            <div className="row">
              <div className="col-md-4">{field({ name: 'amount', label: `You pay${payCode ? ` (${payCode})` : ''}`, type: 'money', required: true })}</div>
              <div className="col-md-4">
                {field({ name: 'exchangeRate', label: `Rate${payCode && receiveCode ? ` (1 ${payCode} = ? ${receiveCode})` : ''}`, type: 'money', required: true })}
                {rateNote}
              </div>
              <div className="col-md-4">
                <div className="form-label">You receive{receiveCode ? ` (${receiveCode})` : ''}</div>
                <div className="rounded border px-3 d-flex justify-content-between align-items-center text-success" style={{ background: 'rgba(25, 135, 84, 0.12)', height: 38 }} data-testid="you-receive">
                  <small>{receiveCode ?? ''}</small>
                  <strong><Amount value={total} /></strong>
                </div>
              </div>
            </div>
            {field({ name: 'remarks', label: 'Remarks (optional)', type: 'textarea', rows: 2, placeholder: 'Add a note if needed' })}
          </div>
        </div>
      </div>
    );

    // the books, as a journal line: the destination is debited what you receive, Cash is credited what you pay the Seller
    const destination = trough === DISPATCHER ? (dispatcherName ?? 'Dispatcher') : trough === TROUGH_BANK ? (bankName ?? 'Bank') : 'Cash';
    const summary = total && payCode && receiveCode && amount ? (
      <span data-testid="purchase-effect">
        Dr {destination} <Amount value={total} code={receiveCode} /> / Cr Cash <Amount value={amount} code={payCode} />{sellerName ? <span className="text-body-secondary"> (paid to {sellerName})</span> : null}
      </span>
    ) : null;

    // the date sits in the modal's title bar, so there is no empty row above the cards
    const headerExtra = <div>{field({ name: 'transactionDate', label: 'Date and time', type: 'datetime', required: true })}</div>;

    return { body, summary, headerExtra, fields: [] };
  },
};
